/**
 * Conversion Tag Parser
 * Extracts REAL conversion tracking tags from HTML
 * No hallucination - parses actual tags from scraped content
 */

export interface ConversionTag {
  platform: 'Google Ads' | 'Google Analytics 4' | 'Google Tag Manager' | 'Facebook Pixel' | 'Other';
  tagId: string;
  type: 'conversion' | 'pageview' | 'event';
  eventName?: string;
  conversionLabel?: string;
  value?: number;
  currency?: string;
  snippetCode: string;  // Actual code found
  isActive: boolean;
  canBeUsed: boolean;  // Can it be used for Google Ads?
  reason?: string;  // Why can/cannot be used
}

export interface ConversionTrackingAnalysis {
  hasTracking: boolean;
  platform: string;
  conversionTags: ConversionTag[];
  googleAdsId?: string;  // AW-XXXXXXXXX
  ga4Id?: string;  // G-XXXXXXXXXX
  gtmId?: string;  // GTM-XXXXXXX
  fbPixelId?: string;
  recommendations: string[];
}

export const conversionTagParser = {
  /**
   * Parse REAL HTML for conversion tracking tags
   */
  parseConversionTags(html: string): ConversionTrackingAnalysis {
    console.log('🔍 Parsing REAL HTML for conversion tags...');
    
    const tags: ConversionTag[] = [];
    const recommendations: string[] = [];
    
    // 1. Google Ads Conversion Tags (AW-XXXXXXXXX)
    const googleAdsRegex = /AW-(\d+)/g;
    const googleAdsMatches = [...html.matchAll(googleAdsRegex)];
    
    let googleAdsId: string | undefined;
    if (googleAdsMatches.length > 0) {
      googleAdsId = `AW-${googleAdsMatches[0][1]}`;
      console.log(`✅ Found Google Ads ID: ${googleAdsId}`);
      
      // Find conversion events
      const conversionEventRegex = /gtag\('event',\s*'conversion',\s*\{[^}]*'send_to':\s*'AW-\d+\/([^']+)'[^}]*\}/g;
      const conversionMatches = [...html.matchAll(conversionEventRegex)];
      
      conversionMatches.forEach(match => {
        const conversionLabel = match[1];
        
        // Extract value and currency if present
        const valueMatch = match[0].match(/'value':\s*(\d+\.?\d*)/);
        const currencyMatch = match[0].match(/'currency':\s*'([^']+)'/);
        
        tags.push({
          platform: 'Google Ads',
          tagId: googleAdsId!,
          type: 'conversion',
          conversionLabel,
          value: valueMatch ? parseFloat(valueMatch[1]) : undefined,
          currency: currencyMatch ? currencyMatch[1] : undefined,
          snippetCode: match[0],
          isActive: true,
          canBeUsed: true,
          reason: 'Can be used directly in campaigns'
        });
      });
      
      recommendations.push(`✅ Google Ads Conversion Tracking active (${conversionMatches.length} conversions found)`);
    } else {
      recommendations.push('⚠️ No Google Ads conversion tracking found. Consider setting up conversion tracking.');
    }
    
    // 2. Google Analytics 4 (G-XXXXXXXXXX)
    const ga4Regex = /G-([A-Z0-9]{10})/g;
    const ga4Matches = [...html.matchAll(ga4Regex)];
    
    let ga4Id: string | undefined;
    if (ga4Matches.length > 0) {
      ga4Id = `G-${ga4Matches[0][1]}`;
      console.log(`✅ Found GA4 ID: ${ga4Id}`);
      
      // Find GA4 events
      const ga4EventRegex = /gtag\('event',\s*'([^']+)'[^)]*\)/g;
      const eventMatches = [...html.matchAll(ga4EventRegex)];
      
      const uniqueEvents = new Set<string>();
      eventMatches.forEach(match => {
        const eventName = match[1];
        if (eventName !== 'conversion' && eventName !== 'config') {
          uniqueEvents.add(eventName);
        }
      });
      
      uniqueEvents.forEach(eventName => {
        tags.push({
          platform: 'Google Analytics 4',
          tagId: ga4Id!,
          type: 'event',
          eventName,
          snippetCode: `gtag('event', '${eventName}')`,
          isActive: true,
          canBeUsed: eventName === 'purchase' || eventName === 'generate_lead' || eventName === 'sign_up',
          reason: eventName === 'purchase' || eventName === 'generate_lead' || eventName === 'sign_up'
            ? 'GA4 event can be imported to Google Ads'
            : 'GA4 event - import to Google Ads for conversion tracking'
        });
      });
      
      recommendations.push(`✅ GA4 installed (${uniqueEvents.size} events). You can import GA4 conversions to Google Ads.`);
    }
    
    // 3. Google Tag Manager (GTM-XXXXXXX)
    const gtmRegex = /GTM-([A-Z0-9]{7})/g;
    const gtmMatches = [...html.matchAll(gtmRegex)];
    
    let gtmId: string | undefined;
    if (gtmMatches.length > 0) {
      gtmId = `GTM-${gtmMatches[0][1]}`;
      console.log(`✅ Found GTM ID: ${gtmId}`);
      
      tags.push({
        platform: 'Google Tag Manager',
        tagId: gtmId,
        type: 'pageview',
        snippetCode: `GTM: ${gtmId}`,
        isActive: true,
        canBeUsed: true,
        reason: 'GTM can manage Google Ads tags. Check GTM container for conversion tags.'
      });
      
      recommendations.push(`✅ GTM installed. Check GTM container for existing conversion tags.`);
    }
    
    // 4. Facebook Pixel
    const fbPixelRegex = /fbq\('init',\s*'(\d+)'/g;
    const fbMatches = [...html.matchAll(fbPixelRegex)];
    
    let fbPixelId: string | undefined;
    if (fbMatches.length > 0) {
      fbPixelId = fbMatches[0][1];
      console.log(`✅ Found Facebook Pixel: ${fbPixelId}`);
      
      tags.push({
        platform: 'Facebook Pixel',
        tagId: fbPixelId,
        type: 'pageview',
        snippetCode: `fbq('init', '${fbPixelId}')`,
        isActive: true,
        canBeUsed: false,
        reason: 'Facebook Pixel cannot be used for Google Ads'
      });
    }
    
    // Summary
    const hasTracking = tags.length > 0;
    const platform = googleAdsId ? 'Google Ads' : 
                     ga4Id ? 'Google Analytics 4' :
                     gtmId ? 'Google Tag Manager' :
                     fbPixelId ? 'Facebook' : 'None';
    
    console.log(`📊 Tracking Analysis: ${tags.length} tags found`);
    console.log(`Platform: ${platform}`);
    
    return {
      hasTracking,
      platform,
      conversionTags: tags,
      googleAdsId,
      ga4Id,
      gtmId,
      fbPixelId,
      recommendations
    };
  },

  /**
   * Get usable conversion tags for Google Ads
   */
  getUsableConversions(analysis: ConversionTrackingAnalysis): ConversionTag[] {
    return analysis.conversionTags.filter(tag => tag.canBeUsed);
  },

  /**
   * Generate recommendation text
   */
  getRecommendationText(analysis: ConversionTrackingAnalysis): string {
    if (!analysis.hasTracking) {
      return '⚠️ No conversion tracking found. You should set up Google Ads conversion tracking for campaign optimization.';
    }
    
    const usable = this.getUsableConversions(analysis);
    
    if (usable.length > 0) {
      return `✅ Found ${usable.length} usable conversion(s). These can be used for campaign optimization.`;
    }
    
    if (analysis.ga4Id) {
      return `ℹ️ GA4 installed. Import GA4 events to Google Ads for conversion tracking.`;
    }
    
    return `⚠️ Tracking found but no usable Google Ads conversions. Set up conversion actions in Google Ads.`;
  }
};

